const EVENT_ENDPOINT = '/api/events';

const EXPERIMENTS = {
  'home-ad-compact-vs-standard': ['control', 'compact'],
  'article-inline-p2-vs-p3': ['control', 'later'],
  'price-guide-result-ad-frequency': ['control', 'dense', 'light']
};

const ARTICLE_INLINE_AD_SELECTOR =
  '.post-content .wla-ad-card[data-wla-lazy-ad], .post__content .wla-ad-card[data-wla-lazy-ad], .kg-canvas .wla-ad-card[data-wla-lazy-ad]';

function getVisitorKey() {
  try {
    const existing = window.localStorage.getItem('wla_monetization_visitor');
    if (existing) return existing;
    const next = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    window.localStorage.setItem('wla_monetization_visitor', next);
    return next;
  } catch {
    return `${navigator.userAgent || 'ua'}:${window.innerWidth || 0}`;
  }
}

function hashString(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function variantForExperiment(experimentKey, variants = []) {
  const safeVariants = variants.length ? variants : EXPERIMENTS[experimentKey] || ['control'];
  const storageKey = `wla_exp_${experimentKey}`;
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (stored && safeVariants.includes(stored)) return stored;
  } catch {}

  const bucket = hashString(`${getVisitorKey()}:${experimentKey}`) % safeVariants.length;
  const variant = safeVariants[bucket] || safeVariants[0] || 'control';
  try {
    window.localStorage.setItem(storageKey, variant);
  } catch {}
  return variant;
}

function variantsFromCard(card, fallbackKey) {
  return String(card.dataset.wlaExperimentVariants || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
    .filter((value, index, all) => all.indexOf(value) === index)
    .concat(EXPERIMENTS[fallbackKey] || [])
    .filter((value, index, all) => all.indexOf(value) === index);
}

function variantSlot(card, variant) {
  const attr = `data-wla-slot-${variant}`;
  return card.getAttribute(attr) || card.dataset.wlaAdSlot || '';
}

function applySlot(card, variant) {
  const slotId = variantSlot(card, variant);
  const slot = card.querySelector('ins.adsbygoogle');
  if (slotId && slot) slot.setAttribute('data-ad-slot', slotId);
}

function markCard(card, experimentKey, variant) {
  card.dataset.wlaExperiment = experimentKey;
  card.dataset.wlaExperimentVariant = variant;
  card.classList.add(`wla-exp-${experimentKey}`);
  card.classList.add(`wla-exp-${experimentKey}--${variant}`);
  applySlot(card, variant);
}

function sendEvent(eventName, card, meta = {}) {
  const experimentKey = card.dataset.wlaExperiment || '';
  const variantKey = card.dataset.wlaExperimentVariant || '';
  if (!experimentKey || !variantKey) return;

  const payload = {
    eventName,
    experimentKey,
    variantKey,
    surface: card.dataset.wlaSurface || '',
    placement: card.dataset.wlaPlacement || card.dataset.wlaAdPosition || '',
    pagePath: `${window.location.pathname}${window.location.search}`,
    pageUrl: window.location.href,
    value: meta.value || 0,
    meta
  };

  try {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      navigator.sendBeacon(EVENT_ENDPOINT, new Blob([body], { type: 'application/json' }));
      return;
    }
    fetch(EVENT_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true
    }).catch(() => {});
  } catch {}
}

function sendOnce(card, eventName, meta = {}) {
  const key = `wlaEvent${eventName.replace(/[^a-z0-9]/gi, '')}`;
  if (card.dataset[key] === 'true') return;
  card.dataset[key] = 'true';
  sendEvent(eventName, card, meta);
}

function articleContentFor(card) {
  return card.closest('.post-content, .post__content, .kg-canvas');
}

function isArticleInlineAd(card) {
  if (!card || !articleContentFor(card)) return false;
  const placement = `${card.dataset.wlaPlacement || ''} ${card.dataset.wlaAdPosition || ''}`;
  return /article-inline/i.test(placement);
}

function moveArticleAdIfNeeded(card, variant) {
  if (variant !== 'later') return;
  const content = articleContentFor(card);
  if (!content) return;
  const paragraphs = Array.from(content.querySelectorAll('p')).filter((paragraph) => paragraph.textContent.trim().length > 80);
  const target = paragraphs[3] || paragraphs[2];
  if (target && target.parentElement && target.nextElementSibling !== card) {
    target.parentElement.insertBefore(card, target.nextSibling);
  }
}

function applyHomeExperiment(card) {
  const experimentKey = 'home-ad-compact-vs-standard';
  const variant = variantForExperiment(experimentKey, variantsFromCard(card, experimentKey));
  markCard(card, experimentKey, variant);
  card.dataset.wlaSurface = card.dataset.wlaSurface || 'home';
  card.dataset.wlaPlacement = card.dataset.wlaPlacement || 'home-inline';
  sendOnce(card, 'experiment_shown', { source: 'theme' });
}

function applyArticleExperiment(card) {
  if (!isArticleInlineAd(card)) return;
  const experimentKey = 'article-inline-p2-vs-p3';
  const variant = variantForExperiment(experimentKey, variantsFromCard(card, experimentKey));
  markCard(card, experimentKey, variant);
  card.dataset.wlaSurface = card.dataset.wlaSurface || 'article';
  card.dataset.wlaPlacement = card.dataset.wlaPlacement || card.dataset.wlaAdPosition || 'article-inline';
  moveArticleAdIfNeeded(card, variant);
  sendOnce(card, 'experiment_shown', { source: 'theme' });
}

function applyPriceGuideExperiment(root) {
  const experimentKey = 'price-guide-result-ad-frequency';
  const variant = variantForExperiment(experimentKey, EXPERIMENTS[experimentKey]);
  root.dataset.wlaExperiment = experimentKey;
  root.dataset.wlaExperimentVariant = variant;
  root.dataset.wlaSurface = 'price-guide';

  const interval = variant === 'dense' ? 8 : variant === 'light' ? 12 : 10;
  const firstAfter = 6;
  Array.from(root.querySelectorAll('[data-price-result-ad]')).forEach((card, index) => {
    card.dataset.priceResultAdAfter = String(firstAfter + index * interval);
    card.dataset.wlaSurface = 'price-guide';
    card.dataset.wlaPlacement = `price-guide-result-${index + 1}`;
    markCard(card, experimentKey, variant);
    sendOnce(card, 'experiment_shown', { source: 'theme', interval, after: card.dataset.priceResultAdAfter });
  });
  window.dispatchEvent(new CustomEvent('wla-monetization-variant-applied', { detail: { experimentKey, variant } }));
}

function observeAdInView() {
  const cards = Array.from(document.querySelectorAll('[data-wla-experiment][data-wla-experiment-variant]'));
  if (!cards.length || !('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
        sendOnce(entry.target, 'ad_inview', { ratio: Math.round(entry.intersectionRatio * 100) / 100 });
        observer.unobserve(entry.target);
      });
    },
    { threshold: [0, 0.5, 1] }
  );
  cards.forEach((card) => observer.observe(card));
}

function trackPriceGuideClicks() {
  document.addEventListener('click', (event) => {
    const productLink = event.target.closest?.('[data-price-product-card] a, [data-offer-row] a');
    if (productLink) {
      const root = productLink.closest('.wla-price-results, .wla-price-product');
      const experimentKey = root?.dataset.wlaExperiment || 'price-guide-result-ad-frequency';
      const variantKey = root?.dataset.wlaExperimentVariant || variantForExperiment(experimentKey, EXPERIMENTS[experimentKey]);
      const card = root || document.body;
      card.dataset.wlaExperiment = experimentKey;
      card.dataset.wlaExperimentVariant = variantKey;
      card.dataset.wlaSurface = 'price-guide';
      card.dataset.wlaPlacement = 'price-guide-product-click';
      sendEvent('price_product_click', card, { href: productLink.href || '' });
    }
  });
}

export function initMonetizationExperiments() {
  window.WLAMonetization = {
    assignExperiment: variantForExperiment,
    sendEvent,
    applySlot
  };

  document.querySelectorAll('.wla-home-ad[data-wla-experiment]').forEach(applyHomeExperiment);
  document.querySelectorAll(ARTICLE_INLINE_AD_SELECTOR).forEach(applyArticleExperiment);
  document.querySelectorAll('.wla-price-results').forEach(applyPriceGuideExperiment);
  observeAdInView();
  trackPriceGuideClicks();
}
